import { useQuery } from 'react-query'
import { request } from '../utils/api'
import { HTTPMethod, Response } from '../utils/api.types'
import type { LiveLabel, LiveStatus } from './useWebinarsQuery'

interface EventDTO {
  name: string
  live_series_id: number
  thumbnail_url: string
  live_status: LiveStatus
  live_label: LiveLabel
  draft_p: boolean
  private_p: boolean
  link: string
  description_html: string
  start_date_epoch: number
  end_date_epoch: number
  speakers?: EventSpeakerDTO[]
  primary_timezone: string
}

export interface EventSpeakerDTO {
  email: string
  live_speaker_id: number
  name: string
  title: string
  company: string
  has_avatar_p: boolean
  hidden_p: boolean
  sortkey: number
  creation_time: string
  avatar_url: string
}

interface GetLiveSeriesListRequest {
  live_series_id?: number
  token?: string
  include_speakers_p?: boolean
  size?: number
}

interface GetLiveSeriesListResponse extends Response {
  data: EventDTO[]
}

export interface EventSpeaker {
  email: string
  liveSpeakerId: number
  name: string
  title: string
  company: string
  hasAvatar: boolean
  isHidden: boolean
  sortKey: number
  creationTime: string
  avatarUrl: string
}

export interface Event {
  // Used for type checking.
  type: 'event'
  name: string
  id: number
  thumbnailUrl: string
  isDraft: boolean
  isPrivate: boolean
  link: string
  descriptionHTML: string
  startDate?: Date
  endDate?: Date
  speakers: EventSpeaker[]
  liveStatus: LiveStatus
  liveLabel: LiveLabel
}

const getLiveSeriesList = (
  id?: number,
  token?: string,
  includeSpeakers?: boolean,
  pageSize?: number
) =>
  request<GetLiveSeriesListRequest, GetLiveSeriesListResponse>({
    url: '/api/2/live/series/list',
    method: HTTPMethod.GET,
    data: {
      live_series_id: id,
      token,
      include_speakers_p: includeSpeakers,
      size: pageSize,
    },
  })

interface UseEventsQueryOptions {
  id?: number
  token?: string
  includeSpeakers?: boolean
  enabled?: boolean
  pageSize?: number
}

const useEventsQuery = (options: UseEventsQueryOptions = {}) => {
  const { id, token, includeSpeakers, enabled = true, pageSize } = options

  return useQuery(
    ['digital-events', { id, token, includeSpeakers, pageSize }],
    async () => {
      const response = await getLiveSeriesList(
        id,
        token,
        includeSpeakers,
        pageSize
      )

      const events = response.data.map<Event>((eventDTO) => {
        const eventSpeakersDTOs = eventDTO.speakers

        const eventSpeakers =
          eventSpeakersDTOs?.map((eventSpeakerDTO) => ({
            email: eventSpeakerDTO.email,
            liveSpeakerId: eventSpeakerDTO.live_speaker_id,
            name: eventSpeakerDTO.name,
            title: eventSpeakerDTO.title,
            company: eventSpeakerDTO.company,
            hasAvatar: eventSpeakerDTO.has_avatar_p,
            isHidden: eventSpeakerDTO.hidden_p,
            sortKey: eventSpeakerDTO.sortkey,
            creationTime: eventSpeakerDTO.creation_time,
            avatarUrl: eventSpeakerDTO.avatar_url,
          })) ?? []

        return {
          type: 'event',
          name: eventDTO.name,
          id: eventDTO.live_series_id,
          thumbnailUrl: eventDTO.thumbnail_url,
          isDraft: eventDTO.draft_p,
          isPrivate: eventDTO.private_p,
          link: eventDTO.link,
          descriptionHTML: eventDTO.description_html,
          startDate: eventDTO.start_date_epoch
            ? new Date(eventDTO.start_date_epoch * 1000)
            : undefined,
          endDate: eventDTO.start_date_epoch
            ? new Date(eventDTO.end_date_epoch * 1000)
            : undefined,
          speakers: eventSpeakers,
          liveStatus: eventDTO.live_status,
          liveLabel: eventDTO.live_label,
        }
      })

      return events
    },
    { enabled }
  )
}

export default useEventsQuery
